import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import type { PositionSlotListItem } from 'domain/shared/positionSlot';
import { useFormContext } from 'framework/form';
import { useEffect, useState } from 'react';
import useMatchingPositionsService from './useMatchingPositionsService';

type MatchingPositionsControlProps = {
  positionSlot: PositionSlotListItem;
};

const MatchingPositionsControl = ({ positionSlot }: MatchingPositionsControlProps) => {
  const { data = {}, onChangeCallback = () => {} } = useFormContext();
  const { getMatchingPositionSlots } = useMatchingPositionsService();
  const [matchingPositionSlotIds, setMatchingPositionSlotIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const selectedMatchingPositionSlotIds = Array.isArray(data.matchingPositionSlotIds)
    ? (data.matchingPositionSlotIds as string[])
    : [];
  const selectedPositionCount = selectedMatchingPositionSlotIds.length + 1;

  useEffect(() => {
    let isCurrent = true;

    setIsLoading(true);

    getMatchingPositionSlots(positionSlot)
      .then(positionSlots => {
        if (!isCurrent) return;

        setMatchingPositionSlotIds(positionSlots.map(({ id }) => id));
        setIsLoading(false);
      })
      .catch(() => {
        if (!isCurrent) return;

        setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
    // The position slot is fixed for the lifetime of the create drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionSlot.id]);

  const addMatchingPosition = () => {
    const nextPositionSlotId = matchingPositionSlotIds.find(id => !selectedMatchingPositionSlotIds.includes(id));
    if (!nextPositionSlotId) return;

    onChangeCallback({
      matchingPositionSlotIds: [...selectedMatchingPositionSlotIds, nextPositionSlotId],
    });
  };

  const removeMatchingPosition = () => {
    onChangeCallback({ matchingPositionSlotIds: selectedMatchingPositionSlotIds.slice(0, -1) });
  };

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2">Searching for matching positions...</Typography>
      </Stack>
    );
  }

  if (!matchingPositionSlotIds.length) {
    return <Typography variant="caption">No matching position found</Typography>;
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
      <IconButton
        size="small"
        aria-label="Remove matching position"
        disabled={selectedMatchingPositionSlotIds.length === 0}
        onClick={removeMatchingPosition}
      >
        <RemoveCircleOutlineOutlinedIcon fontSize="small" />
      </IconButton>
      <Typography>{selectedPositionCount}</Typography>
      <IconButton
        size="small"
        color="primary"
        aria-label="Add matching position"
        disabled={selectedMatchingPositionSlotIds.length === matchingPositionSlotIds.length}
        onClick={addMatchingPosition}
      >
        <AddCircleOutlinedIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ ml: 1 }}>
        {matchingPositionSlotIds.length} other available position
        {matchingPositionSlotIds.length === 1 ? '' : 's'} match these parameters. You can include them in the request.
      </Typography>
    </Stack>
  );
};

export default MatchingPositionsControl;
